import { useEffect, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { X, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useDemo } from "../../context/DemoContext";
import { SourcePill } from "../SourcePill";
import { FrostLayer } from "../FrostLayer";

const People = ({ names }) => (
  <div className="mt-6">
    <p className="text-[12px] font-semibold text-[#777B8D]">Who's involved</p>
    <ul className="mt-2 flex flex-wrap gap-2" data-testid="feed-modal-people">
      {names.map((n) => (
        <li key={n} className="flex items-center gap-2 rounded-full bg-white/70 py-1 pl-1 pr-3 text-[13px] font-medium text-[#14151B]">
          <span className="grid h-7 w-7 place-items-center rounded-full bg-gradient-to-br from-[#A79DE2] to-[#80D9EB] text-[11px] font-bold text-white">
            {n.startsWith("+") ? n : n[0]}
          </span>
          {n.startsWith("+") ? "more friends" : n}
        </li>
      ))}
    </ul>
  </div>
);

export const PreviewModal = () => {
  const { preview, setPreview } = useDemo();
  const [done, setDone] = useState(false);
  useEffect(() => setDone(false), [preview]);
  const item = preview;

  const run = () => {
    setDone(true);
    toast(item.result, { description: "Nothing was actually sent." });
  };

  return (
    <Dialog.Root open={!!item} onOpenChange={(o) => !o && setPreview(null)}>
      <Dialog.Portal>
        <Dialog.Overlay className="modal-overlay fixed inset-0 z-[80]" />
        {item && (
          <Dialog.Content
            data-testid="feed-preview-modal"
            className="glass glass-heavy modal-content fixed left-1/2 top-1/2 z-[90] max-h-[90vh] w-[min(920px,calc(100vw-24px))] -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-[36px] p-3 sm:p-4"
          >
            <div className="grid gap-5 md:grid-cols-[minmax(0,0.9fr)_minmax(0,1fr)]">
              <div className="relative overflow-hidden rounded-[28px]">
                <img src={item.image} alt={item.alt} className="aspect-[4/5] h-full max-h-[44vh] w-full object-cover md:max-h-none" />
                <FrostLayer src={item.image} height="36%" />
                <p className="absolute inset-x-5 bottom-5 z-[2] text-[15px] font-semibold text-white">{item.subtitle}</p>
              </div>
              <div className="flex flex-col px-2 pb-3 pt-1 md:py-6 md:pr-6">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-full bg-[#08090D] px-3 py-1 text-[12px] font-semibold text-white" data-testid="feed-modal-category">{item.category}</span>
                  <SourcePill source={item.source} className="spc-pill--onlight" testId="feed-modal-label" />
                </div>
                <Dialog.Title className="mt-4 text-[34px] font-extrabold leading-[1.02] tracking-[-0.04em] text-[#08090D] sm:text-[42px]" data-testid="feed-modal-title">
                  {item.title}
                </Dialog.Title>
                <p className="mt-2 text-[16px] font-medium text-[#4c5064]">{item.subtitle}</p>
                <Dialog.Description className="mt-4 text-[16px] leading-relaxed text-[#3c4052]">{item.description}</Dialog.Description>
                <People names={item.people} />
                <div className="mt-auto pt-8">
                  {done ? (
                    <p className="flex items-center gap-2 rounded-full bg-white/80 px-5 py-3.5 text-[15px] font-semibold text-[#08090D]" data-testid="feed-modal-result" role="status">
                      <CheckCircle2 size={18} className="text-[#7866E8]" />
                      {item.result}
                    </p>
                  ) : (
                    <button type="button" onClick={run} data-testid="feed-modal-cta" className="btn-primary h-14 w-full text-[16px]">
                      {item.cta}
                    </button>
                  )}
                  <p className="mt-3 text-center text-[11px] text-[#777B8D]">Sample content — fictional demo</p>
                </div>
              </div>
            </div>
            <Dialog.Close aria-label="Close preview" data-testid="feed-modal-close" className="absolute right-5 top-5 grid h-10 w-10 place-items-center rounded-full bg-white/85 text-[#08090D] shadow-md transition-transform hover:scale-105">
              <X size={18} />
            </Dialog.Close>
          </Dialog.Content>
        )}
      </Dialog.Portal>
    </Dialog.Root>
  );
};
